import { reactive } from 'vue'

import { getItem, saveItem } from '@/plugins/storage'
import {
  NO_FILTER_RESULTS,
  MY_CONTENT_SEARCH_ROUTES,
  MY_CONTENT_SEARCH_PERSIST_ROUTES,
} from '@/services/filter-markup-service-constants'
import { log } from '@/services/logging-service'
import { visibleRegion } from '@/services/map-service'
import {
  getCurrentUserProfile,
  type UserProfile,
} from '@/services/profile-service'
import marketingEventService from '@/services/marketing-event-service'
import { getSharedBy, type FILTER_TYPES } from '@/lib/content-events'
import markupLib from '@/lib/markup'
import booleanIntersects from '@turf/boolean-intersects'
import booleanWithin from '@turf/boolean-within'
import simplify from '@turf/simplify'
import { getAllShareGrants } from '@/services/content-sharing-service'
import {
  isLine,
  isTrack,
  isRoute,
  isWaypoint,
} from '@/services/markup-service/helpers'
import { isHiddenByUser } from '@/services/markup-service/visibility-state'
import Color from '@/lib/color'

import type { Markup } from '@/types/markups'
import type { Feature, LineString, Point, Polygon } from 'geojson'

import pubsub from '@/lib/pubsub'
import type { RGBA, Owner, Line } from '@onxmaps/markups-web'

export interface MarkupFilterState {
  searchTerm: string
  hasPhotos: boolean
  contentOwners: string[]
  sharedWith: string[]
  waypointTypes: string[]
  waypointColors: string[]
  onlyOnMap: boolean
  currentUser?: UserProfile
}

export const DEFAULT_FILTER_STATE: MarkupFilterState = Object.freeze({
  searchTerm: '',
  hasPhotos: false,
  contentOwners: [],
  sharedWith: [],
  waypointTypes: [],
  waypointColors: [],
  onlyOnMap: false,
})

const state = reactive({ ...DEFAULT_FILTER_STATE })

// non-observable markup locations store for only-on-map filter
const _markupLocations: { [key: string]: Point | LineString | Polygon } = {}

/**
 * *(AI-generated)*
 * Returns the no-filter-results constant.
 */
export const getNoFilterResults = () => NO_FILTER_RESULTS

/**
 * *(AI-generated)*
 * Returns the current search term filter value.
 */
export const getSearchTermFilter = () => state.searchTerm
/**
 * *(AI-generated)*
 * Returns the current has-photos filter value.
 */
export const getHasPhotosFilter = () => state.hasPhotos
/**
 * *(AI-generated)*
 * Returns the current content-owners filter value.
 */
export const getContentOwnersFilter = () => state.contentOwners
/**
 * *(AI-generated)*
 * Returns the current shared-with filter value.
 */
export const getSharedWithFilter = () => state.sharedWith
/**
 * *(AI-generated)*
 * Returns the current waypoint-type filter value.
 */
export const getWaypointTypeFilter = () => state.waypointTypes
/**
 * *(AI-generated)*
 * Returns the current waypoint-color filter value.
 */
export const getWaypointColorFilter = () => state.waypointColors
/**
 * *(AI-generated)*
 * Returns the current only-on-map filter value.
 */
export const getOnlyOnMapFilter = () => state.onlyOnMap

/**
 * *(AI-generated)*
 * Sets the search term filter.
 */
export const setSearchTermFilter = (searchTerm: string) => {
  state.searchTerm = searchTerm
}
/**
 * *(AI-generated)*
 * Sets the has-photos filter and persists the value.
 */
export const setHasPhotosFilter = (enabled: boolean) => {
  state.hasPhotos = enabled
  saveItem('filters-hasPhotos', enabled)
}
/**
 * *(AI-generated)*
 * Sets the content-owners filter and persists the value.
 */
export const setContentOwnersFilter = async (users: string[]) => {
  state.contentOwners = users
  state.currentUser = await getCurrentUserProfile()
  saveItem('filters-contentOwners', state.contentOwners)
}
/**
 * *(AI-generated)*
 * Sets the shared-with filter and persists the value.
 */
export const setSharedWithFilter = (users: string[]) => {
  state.sharedWith = users
  saveItem('filters-sharedWith', state.sharedWith)
}
/**
 * *(AI-generated)*
 * Sets the waypoint-type filter and persists the value.
 */
export const setWaypointTypeFilter = (types: string[]) => {
  state.waypointTypes = types
  saveItem('filters-waypointTypes', state.waypointTypes)
}
/**
 * *(AI-generated)*
 * Sets the waypoint-color filter and persists the value.
 */
export const setWaypointColorFilter = (colors: string[]) => {
  state.waypointColors = colors
  saveItem('filters-waypointColors', state.waypointColors)
}
/**
 * *(AI-generated)*
 * Sets the only-on-map filter and persists the value.
 */
export const setOnlyOnMapFilter = (enabled: boolean) => {
  state.onlyOnMap = enabled
  saveItem('filters-onlyOnMap', enabled)
}
/**
 * *(AI-generated)*
 * Saves markup geometry locations for viewport visibility checks.
 */
export const setMarkupLocations = (toSave: Array<Markup>) => {
  if (!toSave) {
    return
  }

  toSave.forEach((m) => {
    // Check coordinate length - Lines/Tracks have coordinates property, Routes use GeoJSON
    const coordLength = isRoute(m)
      ? (m.toGeoJSON()?.geometry?.coordinates?.length ?? 0)
      : ((m as unknown as Line)?.coordinates?.length ?? 0)

    if ((isLine(m) || isTrack(m) || isRoute(m)) && coordLength > 20) {
      // dramatically simplify the saved location since we are just trying to
      // see if it is in the viewport
      try {
        const simplifiedGeoJson = simplify(m.toGeoJSON(), { tolerance: 0.001 })
        _markupLocations[m.uuid] = simplifiedGeoJson?.geometry
      } catch (e) {
        log('filter_simplify_line_failed', {
          geo_json: m.toGeoJSON(),
          markup_uuid: m.uuid,
          markup_type: m.type,
          error: e,
        })
      }
    } else {
      _markupLocations[m.uuid] = m.toGeoJSON()?.geometry
    }
  })
}

/**
 * Get the saved markup locations
 */
export const getMarkupLocations = () => {
  return _markupLocations
}

// Makes sure my content search bar is only displayed for all content,
// markup type and select content within these markup types, but not
// for folders and other add-to-folder/share views
/**
 * *(AI-generated)*
 * Returns true if the current route is one that should show the My Content search bar.
 */
export const isOnMyContentSearchRoute = (routeName: string) => {
  return MY_CONTENT_SEARCH_ROUTES.includes(routeName)
}

/**
 * *(AI-generated)*
 * Returns true if the current route should persist the search term.
 */
export const isOnMyContentSearchPersistRoute = (routeName: string) => {
  return MY_CONTENT_SEARCH_PERSIST_ROUTES.includes(routeName)
}

/**
 * Clear the stored filters
 */
export const clearFilters = () => {
  state.hasPhotos = false
  state.contentOwners = []
  state.sharedWith = []
  state.waypointTypes = []
  state.waypointColors = []
  state.onlyOnMap = false
  saveItem('filters-contentOwners', [])
  saveItem('filters-sharedWith', [])
  saveItem('filters-hasPhotos', false)
  saveItem('filters-waypointTypes', [])
  saveItem('filters-waypointColors', [])
  saveItem('filters-onlyOnMap', false)
}

/**
 * Check storage for filters
 */
// eslint-disable-next-line require-await, @typescript-eslint/require-await
export const checkForExistingFilters = async () => {
  state.hasPhotos = getItem('filters-hasPhotos') ?? false
  state.contentOwners = getItem('filters-contentOwners') ?? []
  state.sharedWith = getItem('filters-sharedWith') ?? []
  state.waypointTypes = getItem('filters-waypointTypes') ?? []
  state.waypointColors = getItem('filters-waypointColors') ?? []
  state.onlyOnMap = getItem('filters-onlyOnMap') ?? false
}

/**
 * Called on initial app load to ensure that the stored filters are still valid, and disable any filters that are no
 * longer valid. For example, if "Shared by <User A>" was previously applied, but User A deletes or unshares their
 * markups, the filter is no longer valid and should be disabled. Any filters that are no longer valid are disabled
 * directly in the state variable.
 *
 * @param markups - Complete list of markups
 */
export const checkFilterValidity = async (markups: Array<Markup>) => {
  // Check "Has Photos" filter
  const waypoints = markups.filter((markup) => markup.type === 'waypoint')
  const waypointsWithPhotos = waypoints.filter((markup) =>
    _applyHasPhotosFilter(markup, true)
  )
  if (state.hasPhotos && !waypointsWithPhotos.length) {
    setHasPhotosFilter(false)
  }

  // Check "Waypoint Types" filter
  const waypointTypes = state.waypointTypes.reduce((result: string[], type) => {
    const waypointsOfType = waypoints.filter((w) =>
      _applyWaypointTypeFilter(w, [type])
    )
    if (waypointsOfType.length > 0) {
      result.push(type)
    }
    return result
  }, [])
  setWaypointTypeFilter(waypointTypes)

  // Check "Waypoint Colors" filter
  const waypointColors = state.waypointColors.reduce(
    (result: string[], color) => {
      const waypointsOfColor = waypoints.filter((w) =>
        _applyWaypointColorFilter(w, [color])
      )
      if (waypointsOfColor.length > 0) {
        result.push(color)
      }
      return result
    },
    []
  )
  setWaypointColorFilter(waypointColors)

  // Check "Shared With" filter
  pubsub.subscribe('all-grants-loaded', () => {
    const users = state.sharedWith.reduce((result: string[], user) => {
      const markupsSharedWithUser = markups.filter((m) =>
        _applySharedWithFilter(m, [user])
      )
      if (markupsSharedWithUser.length > 0) {
        result.push(user)
      }
      return result
    }, [])
    setSharedWithFilter(users)
  })

  // Check "Shared By" filter
  const owners = state.contentOwners.reduce((result: string[], user) => {
    const markupsOwnedByUser = markups.filter((m) =>
      _applyContentOwnersFilter(m, [user])
    )
    if (markupsOwnedByUser.length > 0) {
      result.push(user)
    }
    return result
  }, [])
  await setContentOwnersFilter(owners)
}

export interface ApplyFilterOptions {
  /**
   * Whether markups the user has hidden should be dropped while the only-on-map
   * filter is on. Opt in from lists the user is browsing; leave it off anywhere
   * the result drives map visibility, or the hidden markups get re-hidden and
   * the user can never get them back.
   */
  excludeHidden?: boolean
}

/**
 * Apply markup filters to list of markups
 *
 * @param markupsToFilter - Markups to run filters on
 * @param filterState - Current state of the various filters
 * @param options - See ApplyFilterOptions
 * @return list of markup ids that meet filter criteria
 */
export const applyFilters = (
  markupsToFilter: Array<Markup>,
  {
    searchTerm,
    hasPhotos,
    contentOwners,
    sharedWith,
    waypointTypes,
    onlyOnMap,
    waypointColors,
  }: Partial<MarkupFilterState>,
  { excludeHidden = false }: ApplyFilterOptions = {}
) => {
  const filterTimes: {
    markup_filter_load: PerformanceMeasure | null
  } = {
    markup_filter_load: null,
  }

  performance.mark('time_for_filter_markup_load_started')

  const filteredMarkups = markupsToFilter.reduce((acc: string[], markup) => {
    // Filter out markups and get uuid
    if (
      _applySearchTerm(markup, searchTerm) &&
      _applyOnlyOnMapFilter(markup, onlyOnMap, excludeHidden) &&
      _applyHasPhotosFilter(markup, hasPhotos) &&
      _applyContentOwnersFilter(markup, contentOwners) &&
      _applySharedWithFilter(markup, sharedWith) &&
      _applyWaypointTypeFilter(markup, waypointTypes) &&
      _applyWaypointColorFilter(markup, waypointColors)
    ) {
      acc.push(markup.uuid)
    }

    return acc
  }, [])

  performance.mark('time_for_filter_markup_load_finished')

  filterTimes.markup_filter_load = performance.measure(
    'time_for_filter_markup_load',
    'time_for_filter_markup_load_started',
    'time_for_filter_markup_load_finished'
  )
  return filteredMarkups
}

/**
 * Determines whether the search term is in the markup's name
 *
 * @param markupToFilter - Markup to run filter on
 * @param searchTerm - Current search term
 * @returns True if search term is markup name, false otherwise
 */
const _applySearchTerm = (markupToFilter: Markup, searchTerm?: string) => {
  if (!searchTerm || searchTerm.length === 0) {
    return true
  }
  return markupToFilter.name.toLowerCase().search(searchTerm.toLowerCase()) >= 0
}

/**
 * Determines whether the waypoint does not have photos
 *
 * @param markupToFilter - Markup to run filter on
 * @param hasPhotosEnabled - Whether has photos filter is enabled or disabled
 * @return True if waypoint has photos or not hasPhotosEnabled, false otherwise
 */
const _applyHasPhotosFilter = (
  markupToFilter: Markup,
  hasPhotosEnabled?: boolean
) => {
  if (
    hasPhotosEnabled &&
    markupToFilter?.type === 'waypoint' &&
    'photos' in markupToFilter &&
    (!markupToFilter?.photos || markupToFilter?.photos.length === 0)
  ) {
    return false
  }

  return true
}

/**
 * Determines whether the markup is not owned by the specific list of users
 *
 * @param markupToFilter - Markup to run filter on
 * @param contentOwners - List of currently selected users
 * @return True if markup is owned by a contentOwner, false otherwise
 */
const _applyContentOwnersFilter = (
  markupToFilter: Markup,
  contentOwners?: string[]
) => {
  if (!contentOwners || contentOwners.length === 0) {
    return true
  }

  const owner = markupToFilter?.owner as Owner | undefined
  return (
    (owner?.accountId && contentOwners.includes(owner?.accountId)) ||
    shouldShowMyOwnMarkup(markupToFilter, contentOwners)
  )
}

/**
 * Determines whether the markup does not have a grant from current user to a user in the list
 *
 * @param markupToFilter - Markup to run filter on
 * @param sharedWith - List of currently selected users
 * @return True if the markup has a grant to a sharedWith user, false otherwise
 */
const _applySharedWithFilter = (
  markupToFilter: Markup,
  sharedWith?: string[]
) => {
  if (!sharedWith || sharedWith.length === 0) {
    return true
  }

  const grants = getAllShareGrants().fromCurrentUser

  if (!grants) {
    // If grants aren't loaded yet, defer execution until they are
    pubsub.subscribe('all-grants-loaded', () =>
      _rerunSharedWithFilter(markupToFilter, sharedWith)
    )
    return false
  }

  return _rerunSharedWithFilter(markupToFilter, sharedWith)
}

const _rerunSharedWithFilter = (
  markupToFilter: Markup,
  sharedWith?: string[]
) => {
  const grants = getAllShareGrants().fromCurrentUser
  if (!sharedWith) {
    return true
  }

  return grants
    ?.get(markupToFilter.uuid)
    ?.some((grant) =>
      sharedWith.includes((grant as { to: { id: string } }).to.id)
    )
}

/**
 * Determines whether the waypoint has type not in the filter list
 *
 * @param markupToFilter - Markup to run filter on
 * @param waypointTypes - List of currently enabled waypoint types
 * @return True if waypoint type is in waypointTypes list, false otherwise
 */
const _applyWaypointTypeFilter = (
  markupToFilter: Markup,
  waypointTypes?: string[]
) => {
  if (
    isWaypoint(markupToFilter) &&
    waypointTypes &&
    waypointTypes.length > 0 &&
    !waypointTypes.includes(markupToFilter?.properties?.icon ?? '')
  ) {
    return false
  }

  return true
}

/**
 * Determines whether the waypoints has color not in the filter list
 *
 * @param markupToFilter - Markup to run filter on
 * @param waypointColors - List of currently enabled waypoint colors
 * @return True if waypoint has color in the waypointColors list, false otherwise
 */
const _applyWaypointColorFilter = (
  markupToFilter: Markup,
  waypointColors?: string[]
) => {
  // reasons to filter out a markup:
  // - markup is a waypoint
  // - and we are filtering waypoints by color
  // - and the waypoint does not match the legacy color check
  // - and the waypoint's color is not in the filter list
  if (
    markupToFilter?.type === 'waypoint' &&
    waypointColors &&
    waypointColors.length > 0 &&
    !_isWaypointColorLegacyDefault(markupToFilter, waypointColors) &&
    markupToFilter.color
  ) {
    const colorString = Color.fromRgbaObject(
      markupToFilter.color as RGBA
    ).toString()

    if (!waypointColors.includes(colorString)) {
      return false
    }
  }

  return true
}

/**
 * Check if the waypoint is an older waypoint with no color value set AND we are
 * filtering by that vertical's default color.
 *
 * @param markup - Markup to check
 * @param waypointColors - Current enabled waypoint color filters
 * @return True if waypoint has no color and the default color filter is
 *    currently enabled
 */
const _isWaypointColorLegacyDefault = (
  markup: Markup,
  waypointColors: string[]
) => {
  let colorString: string | undefined
  if (markup?.color) {
    colorString = Color.fromRgbaObject(markup.color as RGBA).toString()
  }
  const defaultColor = markupLib.getDefaultStyling('waypoint')?.dbValue
  return !colorString && waypointColors.includes(defaultColor)
}

/**
 * Determines whether the markup is not visible in the map area
 *
 * @param markupToFilter - markup to check
 * @param onlyOnMapEnabled - Whether only on map filter is enabled or disabled
 * @param excludeHidden - Whether markups the user has hidden should be dropped
 * @return True if markup is visible in the map area, false otherwise
 */
const _applyOnlyOnMapFilter = (
  markupToFilter: Markup,
  onlyOnMapEnabled?: boolean,
  excludeHidden?: boolean
) => {
  if (!onlyOnMapEnabled) {
    return true
  }

  /* A markup the user hid isn't on the map, so it has no business in a list of
     what's on screen. This reads the stored choice rather than markup-service's
     effective visibility, which filters themselves overwrite. Callers opt in,
     because "only on map" isn't always the user asking to browse what's on
     screen -- see ApplyFilterOptions. */
  if (excludeHidden && isHiddenByUser(markupToFilter.uuid)) {
    return false
  }

  // FUTURE figure out a way to only have visible region update when panning ends,
  // currently the filter is being constantly recalculated as the map is dragged around
  const region = visibleRegion()
  if (!region) {
    return true
  }

  return (
    _markupLocations.hasOwnProperty(markupToFilter.uuid) &&
    isGeometryInRegion(_markupLocations[markupToFilter.uuid], region)
  )
}

/**
 * Determine if provided geometry is inside visible region (viewport)
 *
 * @param geometry - Geometry to check against region
 * @param region - Region to look for the geometry in (typically viewport)
 * @return - true if in region, false otherwise
 */
export const isGeometryInRegion = (
  geometry: Point | LineString | Polygon | Feature,
  region: Feature<Polygon>
) => {
  try {
    // check if markup is within or intersects the visible region of the map
    return (
      booleanWithin(geometry, region) || booleanIntersects(geometry, region)
    )
  } catch (e) {
    // geometry was likely invalid, handle silently
    return false
  }
}

/**
 * Determine whether the "Me" content owner filter is enabled and whether this markup
 * is owned by the logged in user.
 *
 * @param markup - Individual markup from the list of markups being evaluated
 * @param contentOwners - List of current content owners selected for filtering
 * @return True to show this markup, False to not show.
 */
const shouldShowMyOwnMarkup = (markup: Markup, contentOwners: string[]) => {
  // trying to filter by myself, and the markup either has no owner or I am the owner
  return (
    state.currentUser?.account_id &&
    contentOwners.includes(state.currentUser.account_id) &&
    !markup?.isShared
  )
}

/**
 * Sends appropriate marketing event when a filter has been enabled
 *
 * @param filterType - which type of filter has been applied
 * @param origin - which card view the filter has been triggered from
 * @param [clearButtonClicked = false] - whether clear button has been clicked
 */
export const publishFilterEnabledEvent = (
  filterType: keyof typeof FILTER_TYPES,
  origin: string,
  clearButtonClicked = false
) => {
  const eventProps = {
    filter_type: filterType,
    shared_by: getSharedBy(),
    having_photos_only: getHasPhotosFilter(),
    waypoint_color_count: getWaypointColorFilter().length,
    clear_button: clearButtonClicked,
    waypoint_type_count: getWaypointTypeFilter().length,
    origin: origin,
  }

  marketingEventService.publish('content_list_filter_enabled', eventProps)
}

/**
 * Sends appropriate marketing event when a filter has been cleared
 *
 * @param filterType - which type of filter has been cleared
 * @param origin - which card view the filter has been triggered from
 * @param [clearButtonClicked = false] - whether clear button has been clicked
 */
export const publishFilterClearedEvent = (
  filterType: keyof typeof FILTER_TYPES,
  origin: string,
  clearButtonClicked = false
) => {
  const eventProps = {
    clear_button: clearButtonClicked,
    filter_type: filterType,
    origin: origin,
  }

  marketingEventService.publish('content_list_filter_cleared', eventProps)
}
